/**
 * Maps API calendar events to FullCalendar event inputs, including the "My
 * events" per-game expansion (pure + testable).
 *
 * In the "My events" (mine) scope, a tournament the caller covers at the GAME
 * level is expanded into one dated item per assigned game (from `myGames`)
 * instead of the whole-tournament bar. A tournament the caller only covers at
 * the tournament level (empty `myGames`) still renders as the single bar, as do
 * all events in the "All events" scope. Each expanded game carries the parent
 * tournament as `raw` so a click opens the tournament's games/assignment panel.
 */

import type {
  CalendarEventResponse,
  CallCalendarItem,
  MyTournamentGame,
} from '../../../../types/api';
import { browserTimeZone, wallClockUtcIso } from '../../../../utils/calendarTime';
import type { CalendarScope } from '../../../../services/calendar';

export interface FcEventInput {
  id: string;
  title: string;
  classNames: string[];
  start: string;
  end?: string;
  allDay: boolean;
  /**
   * Exactly one of `raw` (a calendar event) or `call` (a scheduled recruiting call) is set.
   * The click handler branches on which, since they open different surfaces.
   */
  extendedProps: {
    raw?: CalendarEventResponse;
    game?: MyTournamentGame;
    call?: CallCalendarItem;
  };
}

/** Map a single API event to a FullCalendar input honoring the all-day / timezone contract. */
export function toFcEvent(e: CalendarEventResponse): FcEventInput {
  const cls = `ev-${e.eventType.toLowerCase()}`;
  const base = {
    id: e.id,
    title: e.title,
    classNames: [cls],
    extendedProps: { raw: e },
  };
  if (e.isAllDay) {
    // Stored date-at-midnight UTC with an exclusive end; FullCalendar all-day ends are also
    // exclusive, so the stored bounds map directly. Calendar timeZone is UTC.
    return {
      ...base,
      start: e.startsAtUtc.slice(0, 10),
      end: e.endsAtUtc.slice(0, 10),
      allDay: true,
    };
  }
  // Shift the true UTC instant to its event-timezone wall clock so a UTC-configured FullCalendar
  // buckets/displays it on the day it happens in its own timezone.
  return {
    ...base,
    start: wallClockUtcIso(e.startsAtUtc, e.timeZone),
    end: wallClockUtcIso(e.endsAtUtc, e.timeZone),
    allDay: false,
  };
}

/** Map one of the caller's assigned games to a dated FullCalendar item under its tournament. */
function toGameFcEvent(
  tournament: CalendarEventResponse,
  game: MyTournamentGame,
): FcEventInput {
  return {
    id: `game-${game.gameId}`,
    title: `${game.homeTeamNameRaw} vs ${game.awayTeamNameRaw}`.trim(),
    // Tournament color + the "mine" accent since these are, by definition, the caller's games.
    classNames: ['ev-tournament', 'ev-mine'],
    start: wallClockUtcIso(game.startsAtUtc, game.timeZone),
    allDay: false,
    extendedProps: { raw: tournament, game },
  };
}

/**
 * Map a scheduled recruiting call to its calendar chip.
 *
 * A call carries no per-event timezone (unlike a calendar event) — it is a coach-workflow
 * item, so it renders in the VIEWER's zone, which is what the call list already does. Shifting
 * through the browser zone puts it on the day the coach will actually make it: a 9:00 PM
 * Central call on Aug 31 is Sep 1 in UTC, and a UTC-configured FullCalendar would otherwise
 * draw it on the wrong day.
 *
 * The id is prefixed because a call id and an event id are drawn from different tables and
 * FullCalendar requires ids to be unique across the whole event list.
 */
export function toCallFcEvent(call: CallCalendarItem): FcEventInput {
  return {
    id: `call-${call.id}`,
    title: call.prospectName,
    classNames:
      call.status === 'complete' ? ['ev-call', 'ev-call--complete'] : ['ev-call'],
    start: wallClockUtcIso(call.scheduledAt, browserTimeZone()),
    allDay: false,
    extendedProps: { call },
  };
}

/**
 * Build the FullCalendar event list from API events. In mine scope, tournaments
 * the caller covers at the game level expand into their per-game items;
 * everything else maps 1:1.
 *
 * A caller assigned at the tournament (event) level keeps a single bar even when
 * `myGames` is present (a mixed assignment): whole-tournament coverage reads as
 * one event, not a partial per-game expansion. `currentUserId` drives that check.
 *
 * `calls` are the scheduled recruiting calls for the same window, appended as their own
 * chips. Unscheduled calls never reach here — the API omits them, because a call with no
 * time stays on the call list.
 */
export function buildCalendarEvents(
  events: readonly CalendarEventResponse[],
  scope: CalendarScope,
  currentUserId?: string,
  calls: readonly CallCalendarItem[] = [],
): FcEventInput[] {
  return [...buildEventItems(events, scope, currentUserId), ...calls.map(toCallFcEvent)];
}

function buildEventItems(
  events: readonly CalendarEventResponse[],
  scope: CalendarScope,
  currentUserId?: string,
): FcEventInput[] {
  return events.flatMap((e) => {
    const games = e.myGames ?? [];
    const isEventLevelAssignee = e.assignees.some((a) => a.userId === currentUserId);
    if (
      scope === 'mine' &&
      e.eventType === 'Tournament' &&
      games.length > 0 &&
      !isEventLevelAssignee
    ) {
      return games.map((g) => toGameFcEvent(e, g));
    }
    return [toFcEvent(e)];
  });
}
